import axios from "axios";

export const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/**
 * Parse an axios error into a stable {code, message} pair.
 * - Auth-style backend errors return detail={code, message}.
 * - 422 pydantic returns detail=[{loc, msg, ...}].
 * - Network errors have no response at all.
 */
export function extractApiErrorObj(e) {
  if (!e?.response) {
    return { code: "network_error", message: e?.message || "" };
  }
  const status = e.response.status;
  const detail = e.response.data?.detail;

  if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.code) {
    return { code: String(detail.code), message: detail.message || String(detail.code) };
  }
  if (Array.isArray(detail)) {
    const msg = detail.map((x) => x?.msg).filter(Boolean).join(" · ");
    const isEmailErr = detail.some((x) => Array.isArray(x?.loc) && x.loc.includes("email"));
    return { code: isEmailErr ? "invalid_email_format" : "validation_error", message: msg || "Validation error" };
  }
  if (typeof detail === "string") {
    return { code: `http_${status}`, message: detail };
  }
  if (status >= 500) return { code: "server_error", message: "Server error" };
  if (status === 401) return { code: "unauthorized", message: "Unauthorized" };
  if (status === 403) return { code: "forbidden", message: "Forbidden" };
  return { code: `http_${status}`, message: e?.message || "" };
}

// Human string helper (backwards-compat for non-auth screens)
export function extractApiError(e, fallback = "Erreur inattendue") {
  const { message } = extractApiErrorObj(e);
  return message || fallback;
}

/**
 * Translate an axios error via i18n. Falls back to server-provided message,
 * then to a generic key. `t` is the useI18n() hook's translator.
 */
export function translateApiError(e, t, fallbackKey = "auth.err.generic") {
  const { code, message } = extractApiErrorObj(e);
  const key = `auth.err.${code}`;
  const translated = t(key);
  // If translation missing (returns key as-is), fall back to server message, then to fallback key
  if (translated && translated !== key) return translated;
  if (message) return message;
  return t(fallbackKey);
}

// Kept for compatibility with older imports
export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).filter(Boolean).join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "";
}
